import { createSignal, For, onMount, Show, type JSX } from "solid-js";
import Text from "../../components/Text";
import BotSelector from "../../components/BotSelector";
import SellChamberNote from "../../components/inventory/SellChamberNote";
import { EmptyState } from "../../components/EmptyState";
import { showConfirm } from "../../components/Dialog";
import { useToast } from "../../components/MessageBox";
import { useApi, isOk } from "../../api";
import { firstError } from "../../api/envelope";
import { useTranslation } from "../../hooks/useTranslation";
import { type StorageInfo } from "../../services/inventoryApi";
import shared from "./storageShared.module.css";
import styles from "./StorageTasksTab.module.css";

interface UnloadAtConfig {
  id: string;
  name: string;
  storageId: string;
  everythingExceptRepairable: boolean;
  itemKeys: string[];
  isEnabled: boolean;
}

interface Props {
  storage: StorageInfo;
  assignedBotIds: string[];
  canRunTasks: boolean;
  onGoToOverview: () => void;
}

function TaskCard(props: {
  icon: string;
  titleKey: string;
  descKey: string;
  children: JSX.Element;
}) {
  return (
    <div class={`fcard ${styles.taskCard}`}>
      <div class={styles.taskMain}>
        <span class={styles.taskIcon}><i class={`fa-solid ${props.icon}`} /></span>
        <div class={styles.taskText}>
          <div class={styles.taskTitle}><Text val={props.titleKey} /></div>
          <p class={styles.taskDesc}><Text val={props.descKey} /></p>
        </div>
      </div>
      <div class={styles.taskActions}>{props.children}</div>
    </div>
  );
}

export default function StorageTasksTab(props: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  const api = useApi();

  const DEFAULT_SCAN_RADIUS = 32;

  const [scanBotId, setScanBotId] = createSignal("");
  const [scanRadius, setScanRadius] = createSignal(String(DEFAULT_SCAN_RADIUS));
  const [scanLoading, setScanLoading] = createSignal(false);
  const [learnBotId, setLearnBotId] = createSignal("");
  const [learnPlayerName, setLearnPlayerName] = createSignal("");
  const [learnLoading, setLearnLoading] = createSignal(false);
  const [readBotId, setReadBotId] = createSignal("");
  const [readLoading, setReadLoading] = createSignal(false);
  const [suctionBotId, setSuctionBotId] = createSignal("");
  const [suctionLoading, setSuctionLoading] = createSignal(false);
  const [sortBotId, setSortBotId] = createSignal("");
  const [sortLoading, setSortLoading] = createSignal(false);

  const [unloadConfigs, setUnloadConfigs] = createSignal<UnloadAtConfig[]>([]);
  const [showUnloadForm, setShowUnloadForm] = createSignal(false);
  const [unloadFormName, setUnloadFormName] = createSignal("");
  const [unloadFormEverything, setUnloadFormEverything] = createSignal(true);
  const [unloadFormKeys, setUnloadFormKeys] = createSignal("");
  const [unloadEditId, setUnloadEditId] = createSignal<string | null>(null);
  const [unloadBotId, setUnloadBotId] = createSignal("");
  const [unloadSaving, setUnloadSaving] = createSignal(false);

  onMount(() => void loadUnloadConfigs());

  async function loadUnloadConfigs() {
    try {
      const res = await api.inventoryUnloadConfigs.getUnloadAtConfigs();
      if (isOk(res.data)) {
        const all = (res.data.value.configs ?? []) as unknown as UnloadAtConfig[];
        setUnloadConfigs(all.filter(c => c.storageId === props.storage.id));
      }
    } catch (e: any) {
      toast?.error(e?.message ?? t("STORAGE_UNLOAD_LOAD_FAILED"));
    }
  }

  function reportTask(res: { data: unknown }, successMessage: string) {
    const envelope = res.data as { status?: number; errors?: Record<string, string[]> };
    if (isOk(envelope)) toast?.success(successMessage);
    else toast?.error(firstError(envelope) ?? t("STORAGE_UNKNOWN"));
  }

  async function startScan() {
    const parsedRadius = parseInt(scanRadius(), 10);
    const radius = Number.isFinite(parsedRadius) && parsedRadius > 0 ? parsedRadius : DEFAULT_SCAN_RADIUS;
    setScanLoading(true);
    try {
      reportTask(
        await api.inventory.runScan(props.storage.id, { botId: scanBotId() || null, radius }),
        t("STORAGE_SCAN_QUEUED"));
    } catch (e: any) {
      toast?.error(e?.message ?? t("STORAGE_UNKNOWN"));
    } finally {
      setScanLoading(false);
    }
  }

  async function startReadContents() {
    setReadLoading(true);
    try {
      reportTask(await api.inventory.runReadContents(props.storage.id, { botId: readBotId() || null }), t("STORAGE_READ_QUEUED"));
    } catch (e: any) {
      toast?.error(e?.message ?? t("STORAGE_UNKNOWN"));
    } finally {
      setReadLoading(false);
    }
  }

  async function startReadSuction() {
    setSuctionLoading(true);
    try {
      reportTask(
        await api.inventory.runReadInfiniteSettings(props.storage.id, { botId: suctionBotId() || null }),
        t("STORAGE_READ_SUCTION_QUEUED"));
    } catch (e: any) {
      toast?.error(e?.message ?? t("STORAGE_UNKNOWN"));
    } finally {
      setSuctionLoading(false);
    }
  }

  async function startSort() {
    setSortLoading(true);
    try {
      reportTask(await api.inventory.runSort(props.storage.id, { botId: sortBotId() || null }), t("STORAGE_SORT_QUEUED"));
    } catch (e: any) {
      toast?.error(e?.message ?? t("STORAGE_UNKNOWN"));
    } finally {
      setSortLoading(false);
    }
  }

  async function startLearn() {
    const playerName = learnPlayerName().trim();
    if (!playerName) return;
    setLearnLoading(true);
    try {
      reportTask(
        await api.inventory.runLearnChests(props.storage.id, { botId: learnBotId() || null, playerName }),
        t("STORAGE_LEARN_FOLLOWING", playerName));
    } catch (e: any) {
      toast?.error(e?.message ?? t("STORAGE_UNKNOWN"));
    } finally {
      setLearnLoading(false);
    }
  }

  function openUnloadCreate() {
    setUnloadEditId(null);
    setUnloadFormName("");
    setUnloadFormEverything(true);
    setUnloadFormKeys("");
    setShowUnloadForm(true);
  }

  function openUnloadEdit(config: UnloadAtConfig) {
    setUnloadEditId(config.id);
    setUnloadFormName(config.name);
    setUnloadFormEverything(config.everythingExceptRepairable);
    setUnloadFormKeys(config.itemKeys.join(","));
    setShowUnloadForm(true);
  }

  async function saveUnloadConfig() {
    setUnloadSaving(true);
    const body = {
      name: unloadFormName().trim() || t("STORAGE_UNLOAD_DEFAULT_NAME"),
      storageId: props.storage.id,
      everythingExceptRepairable: unloadFormEverything(),
      isEnabled: true,
      itemKeys: unloadFormEverything() ? [] : unloadFormKeys().split(",").map(k => k.trim()).filter(Boolean),
    };
    try {
      const editId = unloadEditId();
      if (editId) await api.inventoryUnloadConfigs.updateUnloadAtConfig(editId, body as never);
      else await api.inventoryUnloadConfigs.createUnloadAtConfig(body as never);
      setShowUnloadForm(false);
      await loadUnloadConfigs();
      toast?.success(t("STORAGE_SAVED"));
    } catch (e: any) {
      toast?.error(e?.message ?? t("STORAGE_SAVE_FAILED"));
    } finally {
      setUnloadSaving(false);
    }
  }

  async function deleteUnloadConfig(id: string) {
    if (!await showConfirm(t("STORAGE_UNLOAD_DELETE_CONFIRM"), true)) return;
    try {
      await api.inventoryUnloadConfigs.deleteUnloadAtConfig(id);
      await loadUnloadConfigs();
    } catch (e: any) {
      toast?.error(e?.message ?? t("STORAGE_DELETE_FAILED"));
    }
  }

  async function runUnloadConfig(id: string) {
    try {
      reportTask(
        await api.inventoryUnloadConfigs.runUnloadAtConfig(id, { botId: unloadBotId() || null }),
        t("STORAGE_UNLOAD_TASK_STARTED"));
    } catch (e: any) {
      toast?.error(e?.message ?? t("STORAGE_UNKNOWN"));
    }
  }

  return (
    <Show when={props.canRunTasks} fallback={<div class={shared.mutedNote}><Text val="STORAGE_READ_ONLY_TASKS" /></div>}>
      <Show when={props.assignedBotIds.length > 0} fallback={
        <EmptyState
          illustration="bots"
          title={t("STORAGE_TASKS_EMPTY_TITLE")}
          hint={t("STORAGE_TASKS_EMPTY_DESC")}
          action={
            <button class="btn btn-primary btn-sm" onClick={props.onGoToOverview}>
              <i class="fa-solid fa-arrow-left" /> <Text val="STORAGE_TASKS_EMPTY_ACTION" />
            </button>
          }
        />
      }>
        <div class={styles.taskSection}>
          <div class={styles.taskSectionTitle}><Text val="STORAGE_TASKS_SECTION_DISCOVERY" /></div>
          <Show when={props.storage.sellChamberBotId}>
            <SellChamberNote storage={props.storage} />
          </Show>

          <div class={styles.taskList}>
            <TaskCard icon="fa-magnifying-glass" titleKey="STORAGE_SCAN_AREA_TITLE" descKey="STORAGE_SCAN_AREA_DESC">
              <div class={styles.taskBot}>
                <span class={styles.taskRunAsLabel}><Text val="STORAGE_TASK_RUN_AS" /></span>
                <BotSelector storageKey="storage_scan_bot" allowedIds={props.assignedBotIds} onSelect={(id) => setScanBotId(id ?? "")} />
              </div>
              <label class={styles.taskRadius}>
                <span class={styles.taskRunAsLabel}><Text val="STORAGE_SCAN_RADIUS" /></span>
                <input
                  class={shared.inlineInput}
                  type="number"
                  min="1"
                  placeholder={String(DEFAULT_SCAN_RADIUS)}
                  value={scanRadius()}
                  onInput={e => setScanRadius(e.currentTarget.value)}
                />
              </label>
              <button class="btn btn-secondary" onClick={() => void startScan()} disabled={scanLoading() || !!props.storage.sellChamberBotId}>
                <Show when={scanLoading()} fallback={<><i class="fa-solid fa-play" /> <Text val="STORAGE_START_SCAN" /></>}>
                  <i class="fa-solid fa-spinner fa-spin" /> <Text val="STORAGE_STARTING" />
                </Show>
              </button>
            </TaskCard>

            <TaskCard icon="fa-graduation-cap" titleKey="STORAGE_LEARN_CHESTS_TITLE" descKey="STORAGE_LEARN_CHESTS_DESC">
              <div class={styles.taskBot}>
                <span class={styles.taskRunAsLabel}><Text val="STORAGE_TASK_RUN_AS" /></span>
                <BotSelector storageKey="storage_learn_bot" allowedIds={props.assignedBotIds} onSelect={(id) => setLearnBotId(id ?? "")} />
              </div>
              <input
                class={`${shared.inlineInput} ${styles.taskInput}`}
                placeholder={t("STORAGE_PLAYER_NAME")}
                value={learnPlayerName()}
                onInput={e => setLearnPlayerName(e.currentTarget.value)}
              />
              <button class="btn btn-secondary" onClick={() => void startLearn()} disabled={learnLoading() || !learnPlayerName().trim() || !!props.storage.sellChamberBotId}>
                <Show when={learnLoading()} fallback={<><i class="fa-solid fa-play" /> <Text val="STORAGE_START_FOLLOWING" /></>}>
                  <i class="fa-solid fa-spinner fa-spin" /> <Text val="STORAGE_STARTING" />
                </Show>
              </button>
            </TaskCard>
          </div>
        </div>

        <div class={styles.taskSection}>
          <div class={styles.taskSectionTitle}><Text val="STORAGE_TASKS_SECTION_MAINTENANCE" /></div>

          <div class={styles.taskList}>
            <TaskCard icon="fa-rotate" titleKey="STORAGE_READ_CONTENTS_TITLE" descKey="STORAGE_READ_CONTENTS_DESC">
              <div class={styles.taskBot}>
                <span class={styles.taskRunAsLabel}><Text val="STORAGE_TASK_RUN_AS" /></span>
                <BotSelector storageKey="storage_read_bot" allowedIds={props.assignedBotIds} onSelect={(id) => setReadBotId(id ?? "")} />
              </div>
              <button class="btn btn-secondary" onClick={() => void startReadContents()} disabled={readLoading()}>
                <Show when={readLoading()} fallback={<><i class="fa-solid fa-play" /> <Text val="STORAGE_READ_CONTENTS" /></>}>
                  <i class="fa-solid fa-spinner fa-spin" /> <Text val="STORAGE_STARTING" />
                </Show>
              </button>
            </TaskCard>

            <TaskCard icon="fa-sliders" titleKey="STORAGE_READ_SUCTION_TITLE" descKey="STORAGE_READ_SUCTION_DESC">
              <div class={styles.taskBot}>
                <span class={styles.taskRunAsLabel}><Text val="STORAGE_TASK_RUN_AS" /></span>
                <BotSelector storageKey="storage_suction_bot" allowedIds={props.assignedBotIds} onSelect={(id) => setSuctionBotId(id ?? "")} />
              </div>
              <button class="btn btn-secondary" onClick={() => void startReadSuction()} disabled={suctionLoading()}>
                <Show when={suctionLoading()} fallback={<><i class="fa-solid fa-play" /> <Text val="STORAGE_READ_SUCTION" /></>}>
                  <i class="fa-solid fa-spinner fa-spin" /> <Text val="STORAGE_STARTING" />
                </Show>
              </button>
            </TaskCard>

            <TaskCard icon="fa-arrow-down-a-z" titleKey="STORAGE_SORT_TITLE" descKey="STORAGE_SORT_DESC">
              <div class={styles.taskBot}>
                <span class={styles.taskRunAsLabel}><Text val="STORAGE_TASK_RUN_AS" /></span>
                <BotSelector storageKey="storage_sort_bot" allowedIds={props.assignedBotIds} onSelect={(id) => setSortBotId(id ?? "")} />
              </div>
              <button class="btn btn-secondary" onClick={() => void startSort()} disabled={sortLoading()}>
                <Show when={sortLoading()} fallback={<><i class="fa-solid fa-play" /> <Text val="STORAGE_START_SORT" /></>}>
                  <i class="fa-solid fa-spinner fa-spin" /> <Text val="STORAGE_STARTING" />
                </Show>
              </button>
            </TaskCard>
          </div>

          <div class={`fcard ${styles.configCard}`}>
            <div class={styles.taskHead}>
              <span><i class="fa-solid fa-box-archive" /> <Text val="STORAGE_UNLOAD_CONFIGS" /> ({unloadConfigs().length})</span>
              <button class="btn btn-secondary btn-sm" onClick={openUnloadCreate}><i class="fa-solid fa-plus" /> <Text val="NEW" /></button>
            </div>
            <p class={styles.taskDesc}><Text val="STORAGE_UNLOAD_CONFIGS_DESC" /></p>

            <Show when={showUnloadForm()}>
              <div class={styles.subForm}>
                <input
                  class={shared.inlineInput}
                  placeholder={t("STORAGE_CONFIG_NAME")}
                  value={unloadFormName()}
                  onInput={e => setUnloadFormName(e.currentTarget.value)}
                />
                <label class={shared.checkRow}>
                  <input type="checkbox" checked={unloadFormEverything()} onChange={e => setUnloadFormEverything(e.currentTarget.checked)} />
                  {" "}<Text val="STORAGE_EVERYTHING_EXCEPT_REPAIRABLE" />
                </label>
                <Show when={!unloadFormEverything()}>
                  <input
                    class={shared.inlineInput}
                    placeholder={t("STORAGE_ITEM_KEYS_PLACEHOLDER")}
                    value={unloadFormKeys()}
                    onInput={e => setUnloadFormKeys(e.currentTarget.value)}
                  />
                </Show>
                <div class="h" style={{ gap: "var(--spacing-sm)", "justify-content": "flex-end" }}>
                  <button class="btn btn-secondary btn-sm" onClick={() => setShowUnloadForm(false)}><Text val="CANCEL" /></button>
                  <button class="btn btn-primary btn-sm" onClick={() => void saveUnloadConfig()} disabled={unloadSaving()}>
                    <Show when={unloadSaving()} fallback={<Text val="SAVE" />}><i class="fa-solid fa-spinner fa-spin" /></Show>
                  </button>
                </div>
              </div>
            </Show>

            <Show when={unloadConfigs().length === 0 && !showUnloadForm()}>
              <div class={shared.mutedNote}><Text val="STORAGE_NO_UNLOAD_CONFIGS" /></div>
            </Show>

            <For each={unloadConfigs()}>
              {(config) => (
                <div class={styles.listRow}>
                  <div class={styles.listRowMain}>
                    <i class="fa-solid fa-box-archive" />
                    <div>
                      <div class={styles.listRowName}>{config.name}</div>
                      <div class={styles.listRowSub}>
                        {config.everythingExceptRepairable
                          ? t("STORAGE_ALL_NON_REPAIRABLE")
                          : t("STORAGE_KEYS", config.itemKeys.join(", ") || "-")}
                      </div>
                    </div>
                  </div>
                  <div class={styles.listRowActions}>
                    <BotSelector storageKey="storage_unload_bot" allowedIds={props.assignedBotIds} onSelect={(id) => setUnloadBotId(id ?? "")} />
                    <button class={shared.iconBtn} onClick={() => void runUnloadConfig(config.id)} title={t("STORAGE_RUN_NOW")}>
                      <i class="fa-solid fa-play" />
                    </button>
                    <button class={shared.iconBtn} onClick={() => openUnloadEdit(config)} title={t("EDIT")}>
                      <i class="fa-solid fa-pen" />
                    </button>
                    <button class={`${shared.iconBtn} ${shared.iconBtnDanger}`} onClick={() => void deleteUnloadConfig(config.id)} title={t("DELETE")}>
                      <i class="fa-solid fa-trash" />
                    </button>
                  </div>
                </div>
              )}
            </For>
          </div>
        </div>
      </Show>
    </Show>
  );
}
